import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid2,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import Icon from "../../../../Icon/Icon";
import { EGInput } from "../../../../MainComponents/Input/EGInput";
import { InvestigationButton, ModalContainer } from "../components";

export const ModalGeneratePDF = ({ show, onHide }) => {
  const theme = useTheme();
  const [t] = useTranslation("investigation");

  const [values, setValues] = useState([]);
  const [password, setPassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [showCopied, setShowCopied] = useState(false);
  const [locked, setLocked] = useState(false);

  useEffect(() => {
    if (!show) {
      setTimeout(() => {
        setValues(sectionsOpts.map((item) => item.value));
        createRandomPassword();
        setShowPassword(false);
        setLocked(false);
      }, 200);
    }
  }, [show]);

  function handleChange(value) {
    if (values.includes(value)) {
      setValues(values.filter((v) => v !== value));
    } else {
      setValues([...values, value]); // Add the value to the array
    }
  }

  const copyPassword = () => {
    navigator.clipboard.writeText(password);
    setShowCopied(true);
    setTimeout(() => {
      setShowCopied(false);
    }, 2000);
  };

  const createRandomPassword = () => {
    const length = 13;
    let result = "";
    let characters =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let charactersLength = characters.length;

    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    setPassword(result);
  };

  const sectionsOpts = [
    { label: t("sections.evaluation"), value: "evaluation" },
    { label: t("sections.locations"), value: "scenarios" },
    { label: t("sections.involved"), value: "involved" },
    { label: t("sections.activity_plan"), value: "plan" },
    { label: t("sections.resources"), value: "resources" },
    { label: t("sections.interviews"), value: "interviews" },
    { label: t("sections.evidences"), value: "evidences" },
    { label: t("sections.conclusion"), value: "conclusions" },
  ];

  const passwordOpts = [
    {
      label: t("modals.generatePDF.create_password"),
      value: "create",
      icon: "outline_refresh",
      onClick: createRandomPassword,
    },
    {
      label: showCopied ? `¡${t("modals.generatePDF.copied")}!` : t("modals.generatePDF.copy_password"),
      value: "copy",
      icon: "outline_copy",
      onClick: copyPassword,
    },
    {
      label: showPassword ? t("modals.generatePDF.hide_password") : t("modals.generatePDF.show_password"),
      value: "show",
      icon: showPassword ? "outline_eye_slash" : "outline_eye",
      onClick: () => setShowPassword(!showPassword),
    },
    {
      label: t("modals.generatePDF.no_password"),
      value: "no",
      icon: "bold_unlock",
      onClick: () => setLocked(!locked),
    },
  ];

  return (
    <ModalContainer show={show} onHide={onHide}>
      {/* Header */}
      <Typography
        fontWeight={700}
        fontSize={28}
        color={theme.palette.ethicsBlue.main}
      >
        {t("modals.generatePDF.title")}
      </Typography>
      <Typography fontSize={16} color="oxfordBlue">
        {t("modals.generatePDF.description")}
      </Typography>

      <hr />

      <Grid2 container spacing={2}>
        <Grid2 size={{ md: 6 }}>
          <Stack>
            {sectionsOpts.map((item) => (
              <CheckboxItem
                key={item.value}
                label={item.label}
                value={item.value}
                checked={values.includes(item.value)}
                onChange={handleChange}
              />
            ))}
          </Stack>
        </Grid2>

        <Grid2 size={{ md: 6 }}>
          <EGInput
            label={t("modals.generatePDF.password")}
            value={password}
            type={showPassword ? "text" : "password"}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Stack direction="row" spacing={2} mt={1} justifyContent="flex-end">
            {passwordOpts.map((item) => (
              <OptionPassword
                key={item.value}
                label={item.label}
                value={item.value}
                icon={item.icon}
                onClick={item.onClick}
              />
            ))}
          </Stack>
        </Grid2>
      </Grid2>

      <Stack direction="row" spacing={2} mt={2} justifyContent="flex-end">
        <InvestigationButton
          label={t("modals.generatePDF.cancel")}
          variant="main"
          outlined
          onClick={onHide}
        />
        <InvestigationButton
          label={t("modals.generatePDF.generate")}
          variant="main"
          onClick={() => {}}
        />
      </Stack>
    </ModalContainer>
  );
};

const CheckboxItem = ({ label, value, checked, onChange, disabled }) => {
  const theme = useTheme();
  return (
    <FormGroup sx={{ width: "fit-content" }}>
      <FormControlLabel
        label={label}
        sx={{
          "& .MuiTypography-root": {
            fontSize: 16,
            fontWeight: 600,
            color: theme.palette.secondary.main,
          },
        }}
        control={
          <Checkbox
            sx={{
              "& .MuiSvgIcon-root": { color: theme.palette.ethicsBlue.main },
            }}
            size="small"
            checked={checked}
            onChange={() => onChange(value)}
            disabled={disabled}
          />
        }
      />
    </FormGroup>
  );
};

const OptionPassword = ({ label, value, icon, onClick }) => {
  
  const theme = useTheme();

  return (
    <Tooltip title={label} arrow>
      <Box onClick={onClick} sx={{ cursor: "pointer" }}>
        <Icon name={icon} color={theme.palette.ethicsBlue.main} />
      </Box>
    </Tooltip>
  );
};
